import React from 'react';
import { getDashboardTickets } from '../../../../../../helpers/front-helper';
import { getUserList } from '../../../../../../helpers/user-helper';
import {
    createEmptyDashboard,
    QUICK_RANGE_OPTIONS,
    FILTER_PRIORITY_OPTIONS,
    FILTER_SLA_MODE_OPTIONS,
    FILTER_SOURCE_OPTIONS,
    FILTER_STATUS_OPTIONS,
    FILTER_TYPE_OPTIONS,
    EMPTY_ASSIGNEE_OPTIONS,
    PRIORITY_LABELS,
    SOURCE_LABELS,
    STATUS_LABELS,
    TYPE_LABELS,
    exportDashboardTicketsExcel,
    exportDashboardTicketsPdf,
} from '../helpers';

const mapLabel = (items, labelsMap = {}) => (items || []).map((item) => ({
    ...item,
    label: labelsMap[item.key] || item.key,
}));

export const useDashboardTickets = ({ token }) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [isLoadingFilters, setIsLoadingFilters] = React.useState(false);
    const [error, setError] = React.useState('');

    const [days, setDays] = React.useState(30);
    const [dateFrom, setDateFrom] = React.useState('');
    const [dateTo, setDateTo] = React.useState('');
    const [status, setStatus] = React.useState('');
    const [priority, setPriority] = React.useState('');
    const [type, setType] = React.useState('');
    const [source, setSource] = React.useState('');
    const [slaMode, setSlaMode] = React.useState('');
    const [assigneeId, setAssigneeId] = React.useState('');
    const [assigneeOptions, setAssigneeOptions] = React.useState(EMPTY_ASSIGNEE_OPTIONS);

    const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = React.useState(false);
    const [dashboard, setDashboard] = React.useState(createEmptyDashboard());

    const quickRangeOptions = React.useMemo(() => QUICK_RANGE_OPTIONS, []);

    const loadFilters = React.useCallback(async () => {
        if (!token) {
            return;
        }

        setIsLoadingFilters(true);
        const usersResp = await getUserList(token, { limit: 500, offset: 0 });

        if (usersResp?.success) {
            const users = Array.isArray(usersResp.data) ? usersResp.data : [];
            setAssigneeOptions([
                ...EMPTY_ASSIGNEE_OPTIONS,
                ...users.map((user) => ({ value: user._id, label: user.name || user.email || 'Usuario' })),
            ]);
        }

        setIsLoadingFilters(false);
    }, [token]);

    const loadDashboard = React.useCallback(async () => {
        if (!token) {
            setError('No se pudo validar la sesión.');
            return;
        }

        setIsLoading(true);
        setError('');

        const params = { days };

        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (status) params.status = status;
        if (priority) params.priority = priority;
        if (type) params.type = type;
        if (source) params.source = source;
        if (slaMode) params.slaMode = slaMode;
        if (assigneeId) params.assigneeId = assigneeId;

        const resp = await getDashboardTickets(token, params);

        if (!resp?.success) {
            setError(resp?.message || 'No se pudo obtener la información del dashboard de tickets.');
            setIsLoading(false);
            return;
        }

        const payload = resp?.data || createEmptyDashboard();
        payload.tickets = payload.tickets || {};
        payload.tickets.byStatus = mapLabel(payload.tickets.byStatus, STATUS_LABELS);
        payload.tickets.byPriority = mapLabel(payload.tickets.byPriority, PRIORITY_LABELS);
        payload.tickets.byType = mapLabel(payload.tickets.byType, TYPE_LABELS);
        payload.tickets.bySource = mapLabel(payload.tickets.bySource, SOURCE_LABELS);

        setDashboard(payload);
        setIsLoading(false);
    }, [token, days, dateFrom, dateTo, status, priority, type, source, slaMode, assigneeId]);

    React.useEffect(() => {
        loadFilters();
    }, [loadFilters]);

    React.useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const hasActiveFilters = Boolean(
        dateFrom || dateTo || status || priority || type || source || slaMode || assigneeId || Number(days) !== 30
    );

    const trendSeriesCreated = dashboard?.tickets?.createdTrend || [];
    const trendSeriesResolved = dashboard?.tickets?.resolvedTrend || [];

    const maxTrendValue = [...trendSeriesCreated, ...trendSeriesResolved].reduce(
        (max, item) => Math.max(max, Number(item.total || 0)),
        0
    );

    const buildTrendPoints = (series) => series
        .map((item, index) => {
            const x = series.length <= 1 ? 0 : (index / (series.length - 1)) * 100;
            const y = maxTrendValue <= 0 ? 100 : 100 - ((Number(item.total || 0) / maxTrendValue) * 100);
            return `${x},${y}`;
        })
        .join(' ');

    const trendPointsCreated = buildTrendPoints(trendSeriesCreated);
    const trendPointsResolved = buildTrendPoints(trendSeriesResolved);

    const formatNumber = React.useCallback((value) => Number(value || 0).toLocaleString('es-MX'), []);
    const formatHours = React.useCallback((value) => `${Number(value || 0).toFixed(2)} h`, []);

    const formatVariation = React.useCallback((value) => {
        const numeric = Number(value || 0);
        const sign = numeric > 0 ? '+' : '';
        return `${sign}${numeric.toFixed(1)}%`;
    }, []);

    const variationClass = React.useCallback((value) => {
        if (value > 0) return 'text-success';
        if (value < 0) return 'text-danger';
        return 'text-muted';
    }, []);

    const handleClearFilters = React.useCallback(() => {
        setDays(30);
        setDateFrom('');
        setDateTo('');
        setStatus('');
        setPriority('');
        setType('');
        setSource('');
        setSlaMode('');
        setAssigneeId('');
    }, []);

    const selectedAssigneeLabel = assigneeOptions.find((item) => item.value === assigneeId)?.label || 'Todos';
    const selectedSlaModeLabel = FILTER_SLA_MODE_OPTIONS.find((item) => item.value === slaMode)?.label || 'Todos';

    const exportFilters = React.useMemo(() => ({
        days,
        dateFrom,
        dateTo,
        status,
        priority,
        type,
        source,
        assigneeLabel: selectedAssigneeLabel,
        slaModeLabel: selectedSlaModeLabel,
    }), [
        days,
        dateFrom,
        dateTo,
        status,
        priority,
        type,
        source,
        selectedAssigneeLabel,
        selectedSlaModeLabel,
    ]);

    const handleExportExcel = React.useCallback(() => {
        exportDashboardTicketsExcel({ dashboard, filters: exportFilters });
    }, [dashboard, exportFilters]);

    const handleExportPdf = React.useCallback(() => {
        exportDashboardTicketsPdf({ dashboard, filters: exportFilters });
    }, [dashboard, exportFilters]);

    return {
        isLoading,
        isLoadingFilters,
        isBusy: isLoading || isLoadingFilters,
        error,
        dashboard,
        days,
        setDays,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        status,
        setStatus,
        priority,
        setPriority,
        type,
        setType,
        source,
        setSource,
        slaMode,
        setSlaMode,
        assigneeId,
        setAssigneeId,
        assigneeOptions,
        isFiltersDrawerOpen,
        setIsFiltersDrawerOpen,
        quickRangeOptions,
        hasActiveFilters,
        trendSeriesCreated,
        trendSeriesResolved,
        trendPointsCreated,
        trendPointsResolved,
        formatNumber,
        formatHours,
        formatVariation,
        variationClass,
        handleClearFilters,
        loadDashboard,
        handleExportExcel,
        handleExportPdf,
        statusOptions: FILTER_STATUS_OPTIONS,
        priorityOptions: FILTER_PRIORITY_OPTIONS,
        typeOptions: FILTER_TYPE_OPTIONS,
        sourceOptions: FILTER_SOURCE_OPTIONS,
        slaModeOptions: FILTER_SLA_MODE_OPTIONS,
    };
};
